/**
 * Structured product attributes for the product form.
 *
 * Rendered from the store's product types: a shoe shop gets Pointure + Type de
 * chaussure, a clothing shop gets Taille, a bag shop gets Type de sac. Required
 * attributes block submission, exactly like the backend enforces.
 *
 * The backend stays authoritative — this mirrors `config/productTaxonomy.js`
 * purely so the form can render instantly.
 */

import React, { useMemo } from "react";
import { Loader2 } from "lucide-react";
import { taxonomyApi, ProductTypeDefinition, ProductAttribute } from "../../api/taxonomy";

interface VariantAttributeFieldsProps {
  productType: string;
  attributes: Record<string, string | number>;
  onChange: (attributes: Record<string, string | number>) => void;
  /** Inform the form which type is selected so it can be saved with the product. */
  onProductTypeChange?: (productType: string) => void;
  disabled?: boolean;
}

function Field({
  attribute,
  value,
  onChange,
  disabled,
}: {
  attribute: ProductAttribute;
  value: string | number | undefined;
  onChange: (value: string | number) => void;
  disabled: boolean;
}) {
  const label = (
    <label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
      {attribute.label}
      {attribute.required ? (
        <span className="text-rose-500" title="Obligatoire">
          *
        </span>
      ) : null}
    </label>
  );

  const base =
    "mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100 disabled:bg-slate-50 disabled:text-slate-400";

  if (attribute.kind === "enum") {
    return (
      <div>
        {label}
        <select
          value={String(value ?? "")}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          className={base}
        >
          <option value="">— Choisir —</option>
          {(attribute.options ?? []).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (attribute.kind === "number") {
    return (
      <div>
        {label}
        <input
          type="number"
          inputMode="decimal"
          min={attribute.min}
          max={attribute.max}
          value={value ?? ""}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          placeholder={attribute.required ? "Obligatoire" : "Optionnel"}
          className={base}
        />
      </div>
    );
  }

  return (
    <div>
      {label}
      <input
        type="text"
        value={String(value ?? "")}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        maxLength={attribute.max}
        placeholder={attribute.required ? "Obligatoire" : "Optionnel"}
        className={base}
      />
    </div>
  );
}

export default function VariantAttributeFields({
  productType,
  attributes,
  onChange,
  onProductTypeChange,
  disabled = false,
}: VariantAttributeFieldsProps) {
  const [types, setTypes] = React.useState<ProductTypeDefinition[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  // Load the catalog profile once; the type list is small and static per store.
  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    taxonomyApi
      .profile()
      .then((profile) => {
        if (!cancelled) setTypes(profile.productTypes || []);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.message || "Catalogue indisponible.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const definition = useMemo(
    () => types.find((type) => type.type === String(productType || "").toUpperCase()),
    [types, productType]
  );

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-3 text-sm text-slate-500">
        <Loader2 size={15} className="animate-spin" />
        Chargement des attributs produit…
      </div>
    );
  }

  /*
   * A store with no configured taxonomy gets no extra fields — the plain product
   * form keeps working exactly as before.
   *
   * The selector must NOT wait for `definition`: choosing the type is precisely
   * what produces one, so short-circuiting on `!definition` would hide it.
   */
  if (error) {
    return <p className="py-2 text-xs text-slate-400">{error}</p>;
  }

  if (types.length === 0) {
    return null;
  }

  const fields = definition ? [...definition.required, ...definition.optional] : [];

  return (
    <div className="space-y-3">
      {/*
        When the shop sells several kinds of things, ask which one this product is
        before showing its attributes — "Chaussures" reveals Pointure, "T-shirt"
        reveals Taille. Guessing would put the wrong fields in front of the merchant.
      */}
      <div>
        <label className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Type de produit
          {definition ? null : <span className="text-rose-500" title="Obligatoire">*</span>}
        </label>
        <select
          value={productType}
          onChange={(event) => {
            const next = event.target.value;
            onProductTypeChange?.(next);
            // Switching type invalidates the previous answers (a pointure is not
            // a clothing size), so start the attributes over.
            onChange({});
          }}
          disabled={disabled}
          className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100 disabled:bg-slate-50 disabled:text-slate-400 sm:max-w-xs"
        >
          <option value="">— Choisir —</option>
          {types.map((type) => (
            <option key={type.type} value={type.type}>
              {type.label}
            </option>
          ))}
        </select>
        {!definition && (
          <p className="mt-1 text-xs text-slate-400">
            Sélectionnez le type pour afficher les champs à renseigner.
          </p>
        )}
      </div>

      {definition ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {fields.map((attribute) => (
            <Field
              key={attribute.key}
              attribute={attribute}
              value={attributes[attribute.key]}
              disabled={disabled}
              onChange={(value) => onChange({ ...attributes, [attribute.key]: value })}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
